import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useEditOwnShiftTimes } from '@/hooks/schedule';
import { useAuth } from '@/hooks/useAuth';

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  employee_name: string;
}

interface EditOwnShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shift: Shift | null;
  currentWeek: Date;
}

const schema = z.object({
  start_time: z.date(),
  end_time: z.date(),
}).refine((data) => data.end_time > data.start_time, {
  message: 'End time must be after start time',
  path: ['end_time'],
});

type FormData = z.infer<typeof schema>;

export function EditOwnShiftDialog({ 
  open, 
  onOpenChange, 
  shift,
  currentWeek 
}: EditOwnShiftDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { profile } = useAuth();
  
  // Use the new edit own shift times mutation (calls dedicated RPC)
  const editOwnShiftMutation = useEditOwnShiftTimes(profile?.company_id, currentWeek);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      start_time: new Date(),
      end_time: new Date(),
    },
  });

  // Format datetime for input
  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    return new Date(value);
  };

  // Reset form when shift changes
  useEffect(() => {
    if (shift && open) {
      form.reset({
        start_time: new Date(shift.start_time),
        end_time: new Date(shift.end_time),
      });
    }
  }, [shift, open, form]);

  const onSubmit = async (data: FormData) => {
    if (!shift || !profile) return;

    // Verify this is the user's own shift (UI-level check, backend enforces too)
    if (shift.profile_id !== profile.id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await editOwnShiftMutation.mutateAsync({
        shiftId: shift.id,
        start: data.start_time,
        end: data.end_time,
      });
      
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the mutation hook via toast
      console.error('Error updating shift:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shift) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Shift Times</DialogTitle>
        </DialogHeader>

        <div className="text-sm text-muted-foreground mb-4">
          <p>Editing shift for {format(new Date(shift.start_time), 'EEEE, MMMM d, yyyy')}</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Time</FormLabel>
                  <FormControl>
                    <Input 
                      type="datetime-local" 
                      value={formatDateTimeLocal(field.value)}
                      onChange={(e) => field.onChange(parseDateTimeLocal(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Time</FormLabel>
                  <FormControl>
                    <Input 
                      type="datetime-local" 
                      value={formatDateTimeLocal(field.value)}
                      onChange={(e) => field.onChange(parseDateTimeLocal(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}